import { apiClient } from '@src/services/http-common'
import {
  DeletePagesPayload,
  DocumentPage,
  DocumentWithPages,
  ReorderPagesPayload,
  RotatePagePayload,
  UpdatePageTypesPayload,
} from '@src/types/documentPage'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const getDocumentPages = async (documentId: string) => {
  const response = await apiClient.get<DocumentPage[]>(`/documents/${documentId}/pages`)
  return response.data
}

export const useDocumentPages = (documentId?: string) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['documentPages', documentId],
    queryFn: () => getDocumentPages(documentId!),
    enabled: !!documentId,
  })

  return [data, isLoading, error] as const
}

const updatePageTypes = async (documentId: string, payload: UpdatePageTypesPayload) => {
  const response = await apiClient.put<DocumentWithPages>(`/documents/${documentId}/pages/type`, payload)
  return response.data
}

const deletePages = async (documentId: string, payload: DeletePagesPayload) => {
  const response = await apiClient.delete<DocumentWithPages>(`/documents/${documentId}/pages`, { data: payload })
  return response.data
}

const reorderPages = async (documentId: string, payload: ReorderPagesPayload) => {
  const response = await apiClient.put<DocumentWithPages>(`/documents/${documentId}/pages/reorder`, payload)
  return response.data
}

const rotatePage = async (documentId: string, pageId: string, payload: RotatePagePayload) => {
  const response = await apiClient.put<DocumentWithPages>(`/documents/${documentId}/pages/${pageId}/rotate`, payload)
  return response.data
}

export const useUpdatePageTypes = (documentId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: UpdatePageTypesPayload) => updatePageTypes(documentId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['fullDocument', documentId], data)
    },
  })
}

export const useDeletePages = (documentId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: DeletePagesPayload) => deletePages(documentId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['fullDocument', documentId], data)
      void queryClient.invalidateQueries({ queryKey: ['documentPages', documentId] })
    },
  })
}

export const useReorderPages = (documentId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ReorderPagesPayload) => reorderPages(documentId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['fullDocument', documentId], data)
      void queryClient.invalidateQueries({ queryKey: ['documentPages', documentId] })
    },
  })
}

export const useRotatePage = (documentId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ pageId, payload }: { pageId: string; payload: RotatePagePayload }) =>
      rotatePage(documentId, pageId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['fullDocument', documentId], data)
    },
  })
}

const regenSAS = async (documentId: string) => {
  const response = await apiClient.post<DocumentWithPages>(`/documents/${documentId}/regenSAS`)
  return response.data
}

export const useRegenSAS = (documentId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => regenSAS(documentId),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['documentPages', documentId] })
      queryClient.setQueryData(['fullDocument', documentId], data)
    },
  })
}
